import { useCallback } from 'react';
import { useMutation } from 'react-relay';
import {
	graphql,
	ConnectionHandler,
	type RecordSourceProxy,
	ConnectionInterface,
} from 'relay-runtime';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type {
	JiraUpdateParentFieldInput,
	parent_issueFieldParent_ParentField_Mutation,
} from '@atlassian/jira-relay/src/__generated__/parent_issueFieldParent_ParentField_Mutation.graphql';
import { useFlagsService } from '@atlassian/jira-flags/src/services/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import RelayDataID from '@atlassian/relay-data-id';
import { ANALYTICS_SOURCE, PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import { useConnectionsList } from '../../controllers/connections-list/index.tsx';
import {
	useUpdateConnectionDetails,
	updateParentHasChildren,
} from '../../controllers/issue-realtime-updater/utils.tsx';
import {
	ACTION_SUBJECT_ID,
	REPARENTING_EXPERIENCE,
	UPDATE_ITEM_PARENT_FAIL_GENERIC_FLAG,
} from './constants.tsx';
import { getFlagDetails } from './utils.tsx';

const JIRA_ISSUE_TYPENAME = 'JiraIssue';

type ReparentProps = {
	issueId: string;
	issueAri: string;
	newParentAri: string | null;
	oldParentId: string | undefined;
	newParentId: string | undefined;
	newParentEdgeId: string | undefined;
	index: number | undefined;
	projectKey?: string;
};

export const useUpdateParent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();

	const onExperienceStart = useExperienceStart({
		experience: REPARENTING_EXPERIENCE,
		analyticsSource: ANALYTICS_SOURCE,
	});

	const onExperienceFail = useExperienceFail({
		experience: REPARENTING_EXPERIENCE,
	});

	const onExperienceSuccess = useExperienceSuccess({
		experience: REPARENTING_EXPERIENCE,
	});

	const { connections } = useConnectionsList();

	const [commitMutation, loading] = useMutation<parent_issueFieldParent_ParentField_Mutation>(
		graphql`
			mutation reparentMutation($input: JiraUpdateParentFieldInput!) {
				jira {
					updateParentField(input: $input) @optIn(to: "JiraIssueFieldMutations") {
						success
						errors {
							extensions {
								statusCode
							}
						}
						field {
							id
							type
							name
						}
					}
				}
			}
		`,
	);

	const handleError = useCallback(
		(error: MutationError) => {
			fireErrorAnalytics({
				meta: {
					id: 'issueTableReparentMutation',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
			onExperienceFail(PACKAGE_NAME, error);

			showFlag(UPDATE_ITEM_PARENT_FAIL_GENERIC_FLAG);
		},
		[onExperienceFail, showFlag],
	);

	const updateConnectionDetails = useUpdateConnectionDetails();

	const updater = useCallback(
		({
			store,
			issueId,
			oldParentId,
			newParentId,
			newParentEdgeId,
			index = 0,
			projectKey,
		}: {
			store: RecordSourceProxy;
			issueId: string;
			oldParentId: string | undefined;
			newParentId: string | undefined;
			newParentEdgeId: string | undefined;
			index: number | undefined;
			projectKey?: string;
		}) => {
			const relayId = RelayDataID({ id: issueId }, JIRA_ISSUE_TYPENAME);
			if (!relayId) {
				return;
			}

			const oldConnection = Object.values(connections).find((connection) => {
				if (oldParentId) {
					return connection.type === 'PARENT_CHILDREN' && connection.parentId === oldParentId;
				}
				return connection.type === 'ROOT_ISSUES';
			});

			const newConnection = Object.values(connections).find((connection) => {
				if (newParentId) {
					return connection.type === 'PARENT_CHILDREN' && connection.parentId === newParentId;
				}
				return connection.type === 'ROOT_ISSUES';
			});

			if (!oldConnection) {
				// Since the issue has to be visible to be repartented, this case should not occur
				return;
			}

			const oldConnectionRecord = store.get(oldConnection.connectionId);
			if (!oldConnectionRecord) {
				return;
			}

			if (newParentEdgeId) {
				updateParentHasChildren(store, newParentEdgeId, true, projectKey);
			}

			if (newConnection) {
				const newConnectionRecord = store.get(newConnection.connectionId);
				if (!newConnectionRecord) {
					return;
				}
				const existingEdge = oldConnectionRecord
					.getLinkedRecords('edges')
					?.find((edge) => edge?.getLinkedRecord('node')?.getDataID() === relayId);

				// no before or after implies an empty conneciton, verify then create the edges list
				const { EDGES } = ConnectionInterface.get();
				const edges = newConnectionRecord.getLinkedRecords(EDGES);
				if (!edges || !existingEdge) {
					return;
				}
				const nextEdges = edges.slice(0, index).concat(existingEdge).concat(edges.slice(index));

				newConnectionRecord.setLinkedRecords(nextEdges, EDGES);

				updateConnectionDetails({
					store,
					connection: newConnection,
					operation: 'add',
					remainingIssueCount: newConnectionRecord.getLinkedRecords('edges')?.length,
					projectKey,
				});
			}

			if (oldConnection) {
				// remove the issue from the old connection
				if (
					oldConnectionRecord
						.getLinkedRecords('edges')
						?.some((edge) => edge?.getLinkedRecord('node')?.getDataID() === relayId)
				) {
					ConnectionHandler.deleteNode(oldConnectionRecord, relayId);

					updateConnectionDetails({
						store,
						connection: oldConnection,
						operation: 'remove',
						remainingIssueCount: oldConnectionRecord.getLinkedRecords('edges')?.length,
						projectKey,
					});
				}
			}
		},
		[connections, updateConnectionDetails],
	);

	const reparent = useCallback(
		({
			issueId,
			issueAri,
			oldParentId,
			newParentId,
			newParentAri,
			newParentEdgeId,
			index,
			projectKey,
		}: ReparentProps) => {
			const input: JiraUpdateParentFieldInput = {
				id: issueAri,
				operation: { operation: 'SET', id: newParentAri },
			};

			onExperienceStart();

			commitMutation({
				variables: {
					input,
				},
				optimisticUpdater: (store) =>
					updater({
						store,
						issueId,
						oldParentId,
						newParentId,
						newParentEdgeId,
						index,
						projectKey,
					}),
				updater: (store, response) => {
					if (response?.jira?.updateParentField?.success) {
						updater({
							store,
							issueId,
							oldParentId,
							newParentId,
							newParentEdgeId,
							index,
							projectKey,
						});
					}
				},
				onCompleted: (response) => {
					const { errors } = response?.jira?.updateParentField ?? {};
					if (errors && errors.length > 0) {
						const error = new MutationError(errors[0]);
						const statusCode = error.statusCode;

						const clientSideErrorMessage = getFlagDetails(statusCode);

						if (clientSideErrorMessage) {
							showFlag(clientSideErrorMessage);
							onExperienceSuccess();
							return;
						}

						handleError(error);
						return;
					}

					fireTrackAnalytics(createAnalyticsEvent({}), 'issue reparented', ACTION_SUBJECT_ID);
					onExperienceSuccess();
				},
				onError: (error: Error) => {
					const err = new MutationError(error);
					handleError(err);
				},
			});
		},
		[
			commitMutation,
			createAnalyticsEvent,
			handleError,
			onExperienceStart,
			onExperienceSuccess,
			showFlag,
			updater,
		],
	);

	return { reparent, loading };
};
